/**
 * Shared pure helpers used across game-loop system modules.
 * No React, no side-effects — all functions are stateless.
 */
import { PLAYER_SIZE } from '../../constants';

/**
 * Push an axis-aligned rectangle (obj) out of an overlapping obstacle.
 * Both are treated as squares. Mutates obj.x / obj.y in place.
 * @param {object} obj       — entity with .x .y
 * @param {object} obstacle  — obstacle with .x .y .size
 * @param {number} objSize   — side length of obj
 * @param {number} obsSize   — side length of obstacle (defaults to obstacle.size)
 */
export function resolveRectOverlap(obj, obstacle, objSize, obsSize) {
  const rs = obsSize ?? obstacle.size;
  if (obj.x >= obstacle.x + rs || obj.x + objSize <= obstacle.x ||
      obj.y >= obstacle.y + rs || obj.y + objSize <= obstacle.y) return; // no overlap
  const ol  = (obj.x + objSize) - obstacle.x;
  const or_ = (obstacle.x + rs) - obj.x;
  const ot  = (obj.y + objSize) - obstacle.y;
  const ob  = (obstacle.y + rs) - obj.y;
  if (Math.min(ol, or_) < Math.min(ot, ob)) {
    obj.x += ol < or_ ? -ol : or_;
  } else {
    obj.y += ot < ob ? -ot : ob;
  }
}

/**
 * Apply damage-type resistances and flat armor reduction.
 * Returns the final (floored ≥ 1) damage value.
 * physRes  — physical damage reduction 0–75% (talent/item sourced)
 */
export function calcDamageReceived(rawHit, dtype, fireRes, lightningRes, armor, physRes = 0) {
  if (dtype === 'fire')      rawHit = Math.round(rawHit * (1 - Math.max(0, Math.min(0.75, fireRes))));
  if (dtype === 'lightning') rawHit = Math.round(rawHit * (1 - Math.max(0, Math.min(0.75, lightningRes))));
  if (dtype === 'physical')  rawHit = Math.round(rawHit * (1 - Math.max(0, Math.min(0.75, physRes))));
  return Math.max(1, rawHit - (armor ?? 0));
}

/**
 * Spawn a ground drop (XP orb or item) into s.groundDrops.
 * If the drop is an item, it is suppressed when its rarity is not in s.lootFilter.
 * XP drops (type === 'xp') are never suppressed.
 */
export function spawnGroundDrop(s, type, x, y, size, item) {
  // Apply loot filter: skip item drops whose rarity is disabled.
  // Set items (rarity === 'set') and runes (slotType === 'rune') always drop.
  if (type !== 'xp' && item && s.lootFilter && s.lootFilter.length > 0) {
    if (item.rarity !== 'set' && item.slotType !== 'rune' && !s.lootFilter.includes(item.rarity)) return;
  }
  const drop = { id: `gd_${++s.nextDropId}`, type, x, y, size };
  if (item) drop.item = item;
  s.groundDrops.push(drop);
}

/**
 * Player centre coordinates (cached once per frame via the assembler).
 */
export function getPlayerCenter(s) {
  return {
    pcx: s.player.x + PLAYER_SIZE / 2,
    pcy: s.player.y + PLAYER_SIZE / 2,
  };
}
